import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WizardStep {
  id: string;
  title: string;
  description: string;
}

interface WizardProgressIndicatorProps {
  steps: WizardStep[];
  currentStep: number;
  completedSteps: number[];
}

const WizardProgressIndicator: React.FC<WizardProgressIndicatorProps> = ({
  steps,
  currentStep,
  completedSteps
}) => {
  return (
    <div className="w-full py-6">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(index);
          const isCurrent = currentStep === index;
          const isUpcoming = currentStep < index;

          return (
            <div key={step.id} className="flex items-center">
              {/* Step Circle */}
              <div
                className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200",
                  {
                    "bg-primary border-primary text-primary-foreground": isCompleted,
                    "bg-primary/10 border-primary text-primary": isCurrent,
                    "bg-muted border-muted-foreground/30 text-muted-foreground": isUpcoming
                  }
                )}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </div>

              {/* Step Info */}
              <div className="ml-3 flex-1">
                <p
                  className={cn(
                    "text-sm font-medium",
                    {
                      "text-primary": isCompleted || isCurrent,
                      "text-muted-foreground": isUpcoming
                    }
                  )}
                >
                  {step.title}
                </p>
                <p className="text-xs text-muted-foreground">{step.description}</p>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-4 transition-all duration-200",
                    {
                      "bg-primary": isCompleted,
                      "bg-muted": !isCompleted
                    }
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WizardProgressIndicator;